import { SyntheticEvent, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { DefaultCustomField, Message, MessageTemplate } from 'src/constants/types';
import {
  getCurrentCompanyAvailabilityStatus,
  getCurrentCompanyId,
} from 'src/modules/companies/slices';
import { useCampaignService } from '../services';
import { ApiError } from 'src/api/axiosClient/types';
import { buildAwayMessageCampaignRequestData } from '../helpers/builders';
import { CampaignListItem } from '../types';
import { useCampaignsApi } from '../api/CampaignsApi';
import { useUserCompaniesService } from 'src/modules/companies/services';
import { useCurrentCompany } from 'src/modules/companies/hooks';

interface Data {
  customFields: DefaultCustomField[];
  messageTemplates: MessageTemplate[];
}

export interface AwayMessageController extends Data {
  isOpen: boolean;
  open: (data: Data, tab: 'custom' | 'recurring', shouldActivateCampaign: boolean) => void;
  close: (isFormDirty?: boolean) => void;
  error: ApiError | null;
  companyAvailabilityStatus: string;
  tab: 'custom' | 'recurring';
  handleTabChange: (_: SyntheticEvent, value: 'custom' | 'recurring') => void;
  submitSaveCustomAwayMessage: (
    awayMessageCampaign: CampaignListItem,
    awayRecurringMessagesCampaign: CampaignListItem,
    text: string,
    shouldActivateCampaign: boolean,
  ) => Promise<void>;
  submitSaveRecurringAwayMessages: (
    awayMessageCampaign: CampaignListItem,
    awayCustomMessageCampaign: CampaignListItem,
    messages: Message[],
    shouldActivateCampaign: boolean,
  ) => Promise<void>;
  isCustomFormDirty: boolean;
  setIsCustomFormDirty: React.Dispatch<React.SetStateAction<boolean>>;
  isRecurringFormDirty: boolean;
  setIsRecurringFormDirty: React.Dispatch<React.SetStateAction<boolean>>;
  shouldActivateCampaign: boolean;
}

export const useAwayMessageController = (): AwayMessageController => {
  const selectedCompanyId = useSelector(getCurrentCompanyId) as string;
  const companyAvailabilityStatus = useSelector(getCurrentCompanyAvailabilityStatus);

  const { getCompanies } = useUserCompaniesService();
  const { refetch: refetchCompany } = useCurrentCompany();

  const { deactivateCampaign: deactivate, activateCampaign: activate } = useCampaignsApi();

  const {
    error,
    createAwayMessageCampaign,
    updateAwayMessageCampaign,
    loading: campaignLoading,
  } = useCampaignService();

  const { getAwayMessageCampaigns } = useCampaignService();

  const [isOpenDrawer, setIsOpenDrawer] = useState<boolean>(false);
  const [messageTemplates, setMessageTemplates] = useState<MessageTemplate[]>([]);
  const [customFields, setCustomFields] = useState<DefaultCustomField[]>([]);
  const [tab, setTab] = useState<'custom' | 'recurring'>('custom');

  const [isCustomFormDirty, setIsCustomFormDirty] = useState<boolean>(false);
  const [isRecurringFormDirty, setIsRecurringFormDirty] = useState<boolean>(false);
  const [shouldActivateCampaign, setShouldActivateCampaign] = useState<boolean>(false);

  const open = useCallback(
    async (data: Data, tab: 'custom' | 'recurring' = 'custom', shouldActivateCampaign = false) => {
      setTab(tab);
      setCustomFields(data.customFields);
      setMessageTemplates(data.messageTemplates);
      setShouldActivateCampaign(shouldActivateCampaign);
      setIsOpenDrawer(true);
    },
    [],
  );

  const close = useCallback(() => {
    if (!isCustomFormDirty && !isRecurringFormDirty) {
      setShouldActivateCampaign(false);
      setIsOpenDrawer(false);
    }
  }, []);

  const handleTabChange = useCallback((_: SyntheticEvent, value: 'custom' | 'recurring') => {
    setTab(value);
  }, []);

  const refreshCampaigns = async (): Promise<void> => {
    await Promise.all([
      await getCompanies(),
      await refetchCompany(),
      await getAwayMessageCampaigns(selectedCompanyId),
    ]);
  };

  const submitSaveCustomAwayMessage = useCallback(
    async (
      awayMessageCampaign: CampaignListItem,
      awayRecurringMessagesCampaign: CampaignListItem,
      text: string,
      shouldActivateCampaign: boolean,
    ) => {
      const { scheduledMessages } = awayMessageCampaign;
      const updatedMessages = scheduledMessages.map(m => {
        if (m.scheduledType === 'immediate') {
          return { ...m, text };
        } else {
          return { ...m };
        }
      });
      const requestData = buildAwayMessageCampaignRequestData(updatedMessages, 'custom');
      if (awayMessageCampaign.id) {
        await updateAwayMessageCampaign(selectedCompanyId, awayMessageCampaign.id, requestData);
        if (!text) {
          await deactivate(selectedCompanyId, [awayMessageCampaign.id]);
        } else {
          if (shouldActivateCampaign) {
            awayRecurringMessagesCampaign.id &&
              (await deactivate(selectedCompanyId, [awayRecurringMessagesCampaign.id]));
            await activate(selectedCompanyId, [awayMessageCampaign.id]);
          }
        }
      } else {
        const newCampaignId = await createAwayMessageCampaign(selectedCompanyId, requestData);
        if (shouldActivateCampaign) {
          awayRecurringMessagesCampaign.id &&
            (await deactivate(selectedCompanyId, [awayRecurringMessagesCampaign.id]));
          await activate(selectedCompanyId, [newCampaignId]);
        }
      }
      setIsRecurringFormDirty(false);
      setIsCustomFormDirty(false);
      close();
      await refreshCampaigns();
    },
    [],
  );

  const submitSaveRecurringAwayMessages = useCallback(
    async (
      awayMessageCampaign: CampaignListItem,
      awayCustomMessageCampaign: CampaignListItem,
      messages: Message[],
      shouldActivateCampaign: boolean,
    ) => {
      let startPosition = 1;
      const messagesWithUpdatedPositions = messages.map(m => {
        return { ...m, position: startPosition++ };
      });

      const requestData = buildAwayMessageCampaignRequestData(
        messagesWithUpdatedPositions,
        'recurring',
      );
      if (awayMessageCampaign.id) {
        await updateAwayMessageCampaign(selectedCompanyId, awayMessageCampaign.id, requestData);
        if (messages.length === 0) {
          await deactivate(selectedCompanyId, [awayMessageCampaign.id]);
        } else {
          if (shouldActivateCampaign) {
            awayCustomMessageCampaign.id &&
              (await deactivate(selectedCompanyId, [awayCustomMessageCampaign.id]));
            await activate(selectedCompanyId, [awayMessageCampaign.id]);
          }
        }
      } else {
        const newCampaignId = await createAwayMessageCampaign(selectedCompanyId, requestData);
        if (shouldActivateCampaign) {
          awayCustomMessageCampaign.id &&
            (await deactivate(selectedCompanyId, [awayCustomMessageCampaign.id]));
          await activate(selectedCompanyId, [newCampaignId]);
        }
      }
      setIsRecurringFormDirty(false);
      setIsCustomFormDirty(false);
      close();
      await refreshCampaigns();
    },
    [],
  );

  return {
    isOpen: isOpenDrawer && !campaignLoading.getAwayMessageCampaigns,
    close,
    open,
    error,
    customFields,
    messageTemplates,
    shouldActivateCampaign,
    companyAvailabilityStatus,
    tab,
    handleTabChange,
    submitSaveCustomAwayMessage,
    submitSaveRecurringAwayMessages,
    isCustomFormDirty,
    setIsCustomFormDirty,
    isRecurringFormDirty,
    setIsRecurringFormDirty,
  };
};
